import { achievements, AUDIENCES, C, formats, mechanics, type Achievement } from "@bd/content";
import { yearOf } from "./calendar";
import type { Company, GameState } from "./state";
import { isUnlocked } from "./unlocks";
import { log } from "./util";

/** Turn this company's new log entries into "log:<kind>" flags, so achievements can ask "did X ever happen to you?". */
function absorbLog(state: GameState, co: Company): void {
  const from = co.director.logCursor ?? 0;
  for (let i = from; i < state.log.length; i++) {
    const e = state.log[i];
    if (e.company !== co.id) continue;
    const f = `log:${e.kind}`;
    if (!co.flags.includes(f)) co.flags.push(f);
  }
  co.director.logCursor = state.log.length;
}

export function achievementMet(state: GameState, co: Company, a: Achievement): boolean {
  const c = a.cond;
  const released = co.products.filter((p) => p.launched);
  if (c.stat) return ((co.stats as unknown as Record<string, number>)[c.stat] ?? 0) >= c.gte;
  if (c.bestScore !== undefined) return released.some((p) => p.score >= c.bestScore);
  if (c.worstScore !== undefined) return released.some((p) => p.score < c.worstScore);
  if (c.evergreens !== undefined) return released.filter((p) => p.evergreen).length >= c.evergreens;
  if (c.copies !== undefined) return released.reduce((s, p) => s + p.sold, 0) >= c.copies;
  if (c.oneGameCopies !== undefined) return released.some((p) => p.sold >= c.oneGameCopies);
  if (c.cash !== undefined) return co.cash >= c.cash;
  if (c.level) return ["garage", "office", "studio", "hq"].indexOf(co.level) >= ["garage", "office", "studio", "hq"].indexOf(c.level);
  if (c.staff !== undefined) return co.staff.length >= c.staff;
  if (c.fans !== undefined) return co.fans >= c.fans;
  if (c.combos !== undefined) return co.stats.combosFound >= c.combos;
  if (c.allAudiences) return AUDIENCES.every((au) => released.some((p) => p.design.audience === au));
  if (c.allFormats) return formats.every((f) => released.some((p) => p.design.format === f.id));
  if (c.themeGames) return released.filter((p) => p.design.theme === c.themeGames).length >= (c.gte ?? 1);
  if (c.researched !== undefined) return co.researched.length + co.unlocked.filter((u) => !u.startsWith("p:")).length - (co.director.startUnlocks ?? 0) >= c.researched;
  if (c.playtests !== undefined) return co.flags.includes("log:playtest") || co.products.some((p) => p.pending?.playtests.length) || co.projects.some((p) => p.playtests.length >= c.playtests);
  if (c.log) return co.flags.includes(`log:${c.log}`);
  if (c.pets !== undefined) return co.pets.length >= c.pets;
  if (c.rooms !== undefined) return co.rooms.length >= c.rooms;
  if (c.room) return co.rooms.some((r) => r.type === c.room);
  if (c.kind) return released.some((p) => p.kind === c.kind);
  if (c.noLoansBy !== undefined) return yearOf(state.week) >= c.noLoansBy && !co.flags.includes("tookLoan");
  if (c.garageHit) return co.flags.includes("garageHit");
  if (c.longParty) return released.some((p) => p.design.audience === "party" && p.design.playTime > 120);
  if (c.combo) return released.some((p) => p.design.mechanic === c.combo[0] && p.design.audience === c.combo[1]);
  if (c.cheap !== undefined) return released.some((p) => p.price < c.cheap);
  if (c.hypeGap) return released.some((p) => p.inputs.H >= 0.3);
  if (c.flag) return co.flags.includes(c.flag);
  if (c.unlocked) return isUnlocked(co, c.unlocked);
  if (c.hiddenMechanics !== undefined) return mechanics.filter((m) => m.hidden && isUnlocked(co, `m:${m.id}`)).length >= c.hiddenMechanics;
  if (c.arcs !== undefined) return co.stats.arcsDone >= c.arcs;
  if (c.eventsAnswered !== undefined) return co.stats.eventsAnswered >= c.eventsAnswered;
  if (c.year !== undefined) return yearOf(state.week) >= c.year;
  if (c.rivalBust) return state.rivals.some((r) => r.bust);
  return false;
}

/** Check every achievement the company doesn't have yet. Runs every few weeks, and is cheap. */
export function checkAchievements(state: GameState, co: Company): string[] {
  absorbLog(state, co);
  if (co.level === "garage" && co.products.some((p) => p.launched && p.score >= 8)) {
    if (!co.flags.includes("garageHit")) co.flags.push("garageHit");
  }
  if (co.loans.some((l) => l.lender === "bank") && !co.flags.includes("tookLoan")) co.flags.push("tookLoan");
  const have = new Set(co.achievements.map((a) => a.id));
  const fresh: string[] = [];
  for (const a of achievements) {
    if (have.has(a.id)) continue;
    if (!achievementMet(state, co, a)) continue;
    co.achievements.push({ id: a.id, week: state.week });
    fresh.push(a.id);
    log(state, "achievement", `${C.achievement[a.id].name}: ${a.desc}`, co.id);
  }
  return fresh;
}
